/**
 * "Add account" — for places the depletions feed doesn't have yet.
 *
 * The rep is standing in a bar that isn't in their list and needs
 * somewhere to log the visit. The hard part isn't the form, it's not
 * creating a second copy of an account that already exists under a
 * slightly different name — the feed's "JOE'S BAR #114" against a
 * rep's "Joes Bar".
 *
 * So the flow is search-first:
 *
 *   1. As the rep types a name (debounced), we search for near matches
 *      and show them inline. Picking one navigates to that account —
 *      which is the outcome we actually want, since it already exists.
 *   2. If they submit anyway, the backend re-checks and 409s with the
 *      candidates unless `confirm_despite_matches` is set. The client
 *      can't skip the step.
 *   3. Only then do we create, tagged `source: 'rep'` until a
 *      depletions file confirms it.
 *
 * Any rep or admin may add an account in any state — reps work just
 * outside their assigned territory often enough that gating on it
 * would block a real visit. The backend keeps the creator's own
 * accounts visible to them regardless of territory.
 */

import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  createAccount,
  searchAccounts,
  type AccountMatch,
  type CreateAccountPayload,
} from '../../api/field';
import { ApiError } from '../../api/client';
import { US_STATES } from '../../util/usStates';
import { ContactsEditor } from './ContactsEditor';
import {
  draftsToPayload,
  emptyContactDraft,
  hasInvalidEmail,
  type ContactDraft,
} from './contactDrafts';
import { FIELD_SX, GOLD, HAIRLINE, HELPER_SX, INK, IVORY, FONT_INTER } from './formStyles';

/** Wait this long after the last keystroke before searching. Long
 *  enough not to fire per character, short enough that matches are on
 *  screen before the rep finishes filling in the address. */
const SEARCH_DEBOUNCE_MS = 250;

/** Below this, a query matches so much that the list is noise.
 *  Two is enough for the backend's prefix match to be useful. */
const MIN_SEARCH_LENGTH = 2;

/** A quiet rule + caption that turns a run of inputs into named
 *  groups. Fifteen boxes in a column is a chore; three short sections
 *  is a form someone can see the end of. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" gap={1.25} sx={{ mt: 0.5 }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'rgba(15, 10, 7, 0.4)',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: HAIRLINE }} />
    </Stack>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired with the new (or matched) account id, so the caller can
   *  navigate straight to it. */
  onCreated: (accountId: number, name: string) => void;
  /** Pre-fills the state picker with the rep's first territory — the
   *  overwhelmingly common case. */
  defaultStateCode?: string | null;
}

export function AddAccountDialog({ open, onClose, onCreated, defaultStateCode }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState(defaultStateCode ?? '');
  const [zip, setZip] = useState('');
  const [premises, setPremises] = useState<'' | 'ON' | 'OFF'>('');
  const [notes, setNotes] = useState('');
  // One card per person. Starts with a single empty one: the rep
  // usually has exactly one contact, and a card that's never filled
  // in is discarded server-side rather than saved as a blank row.
  const [contacts, setContacts] = useState<ContactDraft[]>([emptyContactDraft()]);

  const [debouncedName, setDebouncedName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [serverMatches, setServerMatches] = useState<AccountMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset everything when the dialog opens, so a previous abandoned
  // attempt doesn't bleed into the next one.
  useEffect(() => {
    if (!open) return;
    setName('');
    setAddress('');
    setCity('');
    setStateCode(defaultStateCode ?? '');
    setZip('');
    setPremises('');
    setNotes('');
    setContacts([emptyContactDraft()]);
    setDebouncedName('');
    setServerMatches(null);
    setError(null);
  }, [open, defaultStateCode]);

  // Debounce the name into the search query.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedName(name.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [name]);

  const searchQuery = useQuery({
    queryKey: ['field', 'account-search', debouncedName],
    queryFn: () => searchAccounts(debouncedName),
    enabled: open && debouncedName.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
  });

  // Live matches while typing; server-returned ones after a blocked
  // submit (which are authoritative for that exact payload).
  const matches = serverMatches ?? searchQuery.data?.matches ?? [];
  const hasExact = matches.some((m) => m.is_exact_normalised);

  // Says the useful thing at each stage rather than one static line:
  // what to do, then whether anything matched.
  const searched = debouncedName.length >= MIN_SEARCH_LENGTH && !searchQuery.isFetching;
  const helperMessage =
    matches.length > 0
      ? 'Pick from the list if it already exists.'
      : searched
        ? 'No existing account matches — this will create a new one.'
        : 'Start typing to search existing accounts.';

  // Blocked rather than left to the API: a 422 on a mistyped address
  // would fail the whole account, and the rep can't tell which field
  // caused it.
  const emailInvalid = hasInvalidEmail(contacts);

  const canSubmit = name.trim().length > 0 && !emailInvalid && !submitting;

  const submit = async (confirmDespiteMatches: boolean) => {
    setSubmitting(true);
    setError(null);

    const payload: CreateAccountPayload = {
      name: name.trim(),
      address: address.trim() || null,
      city: city.trim() || null,
      state_code: stateCode || null,
      zip_code: zip.trim() || null,
      premises_type: premises || null,
      notes: notes.trim() || null,
      contacts: draftsToPayload(contacts),
      confirm_despite_matches: confirmDespiteMatches,
    };

    try {
      const detail = await createAccount(payload);
      onCreated(detail.summary.account_id, detail.summary.name);
    } catch (e) {
      // 409 means "look at these first". Surface them rather than
      // showing a generic failure — the rep can still pick one.
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body?.detail;
        const found =
          typeof body === 'object' && body !== null && 'matches' in body
            ? ((body as { matches?: AccountMatch[] }).matches ?? [])
            : [];
        setServerMatches(found);
        setError(null);
      } else {
        setError('Couldn’t add that account. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stateOptions = useMemo(() => US_STATES, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          border: fullScreen ? 'none' : `1px solid ${HAIRLINE}`,
          // A single soft shadow rather than MUI's stacked default —
          // the dialog should sit above the page, not punch a hole in it.
          boxShadow: fullScreen ? 'none' : '0 24px 60px -20px rgba(15, 10, 7, 0.35)',
          // Full-bleed on a phone, but never edge-to-edge on desktop.
          m: fullScreen ? 0 : 2,
          maxHeight: fullScreen ? '100%' : 'calc(100% - 32px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: IVORY,
          borderBottom: `1px solid ${HAIRLINE}`,
          py: { xs: 1.75, sm: 2 },
          px: { xs: 2.5, sm: 3 },
          // The form is taller than a phone screen; the header naming
          // what's being filled in shouldn't scroll away from it.
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: GOLD,
            mb: 0.25,
          }}
        >
          New Account
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: { xs: 17, sm: 18 },
            letterSpacing: '-0.01em',
            color: INK,
          }}
        >
          Add an Account
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '22px !important', px: { xs: 2.5, sm: 3 }, pb: 1 }}>
        <Stack gap={2}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
              {error}
            </Alert>
          )}

          {/* The account name is a picker, not a free text box.
              Existing accounts filter down as the rep types, with their
              addresses, so a duplicate is visible BEFORE it's created —
              which is the only reliable way to stop one. Picking an
              existing account opens it instead.

              freeSolo because the whole point is that they may be
              adding something genuinely new; the typed value is the
              name in that case. */}
          <Autocomplete
            freeSolo
            options={matches}
            // Controlled `open`, because freeSolo suppresses MUI's
            // "no options" message but still renders the popup. An
            // empty bordered Paper collapses to a 1px rule sitting
            // under the field — it reads as a stray underline. Only
            // open when there is actually something to show.
            open={pickerOpen && matches.length > 0}
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            // The server already filtered and ranked. Filtering again
            // client-side would drop fuzzy hits ("Joes Bar" against
            // "JOE'S BAR #114") whose characters don't literally match.
            filterOptions={(x) => x}
            inputValue={name}
            onInputChange={(_, value, reason) => {
              // `reset` fires when an option is picked; that path
              // navigates away, so don't clobber the field with it.
              if (reason === 'reset') return;
              setName(value);
              // Any edit invalidates the server's verdict.
              setServerMatches(null);
            }}
            onChange={(_, value) => {
              // An object means they picked an existing account —
              // open it rather than creating a near-duplicate.
              if (value && typeof value !== 'string') {
                onCreated(value.account_id, value.name);
              }
            }}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
            isOptionEqualToValue={(a, b) =>
              typeof a !== 'string' && typeof b !== 'string' && a.account_id === b.account_id
            }
            loading={searchQuery.isFetching}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 1.5,
                  // Lifts the list off the form rather than letting it
                  // sit flat against the next field.
                  boxShadow: '0 6px 20px rgba(15, 10, 7, 0.10)',
                  '& .MuiAutocomplete-listbox': { maxHeight: 280, py: 0.5 },
                },
              },
            }}
            renderOption={(props, option) => {
              if (typeof option === 'string') return null;
              const location =
                [option.address, option.city, option.state_code].filter(Boolean).join(' · ') ||
                'No address on file';
              return (
                <Box component="li" {...props} key={option.account_id} sx={{ py: 0.75 }}>
                  <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
                    <StorefrontIcon
                      sx={{ fontSize: 16, color: 'rgba(15,10,7,0.4)', flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontFamily: FONT_INTER, fontSize: 13.5, fontWeight: 600, color: INK }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15,10,7,0.5)' }}
                      >
                        {location}
                        {option.source === 'rep' ? ' · manually added' : ''}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account name"
                placeholder="Search existing accounts, or type a new name"
                size="small"
                fullWidth
                autoFocus
                required
                helperText={helperMessage}
                FormHelperTextProps={HELPER_SX}
                sx={FIELD_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchQuery.isFetching ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* One-line warning for the case the dropdown can't make
              obvious on its own: a name that differs from an existing
              account only by case or punctuation. Creating alongside
              one of those is almost always a mistake. */}
          {hasExact && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{ fontFamily: FONT_INTER, fontSize: 12.5, py: 0.25 }}
            >
              An account with this name already exists — pick it from the list rather than adding a
              second one.
            </Alert>
          )}

          <SectionLabel>Location</SectionLabel>

          {/* Grid rather than a row of flex children: the columns stay
              on the same edges whatever the field widths are, which is
              what makes the block read as aligned instead of nearly
              aligned. */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <TextField
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              size="small"
              fullWidth
              sx={FIELD_SX}
            />
            <TextField
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              size="small"
              fullWidth
              sx={FIELD_SX}
            />
          </Box>

          {/* State and ZIP are fixed-width because their content is:
              two letters and five digits. Premises takes the rest, and
              drops to its own full-width row on a phone rather than
              being squeezed into a third of it. */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr 1fr', sm: '104px 116px 1fr' },
            }}
          >
            <TextField
              label="State"
              select
              value={stateCode}
              onChange={(e) => {
                setStateCode(e.target.value);
                setServerMatches(null);
              }}
              size="small"
              fullWidth
              sx={FIELD_SX}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {stateOptions.map((s) => (
                <MenuItem key={s.code} value={s.code}>
                  {s.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="ZIP"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
              sx={FIELD_SX}
            />
            <TextField
              label="Premises"
              select
              value={premises}
              onChange={(e) => setPremises(e.target.value as '' | 'ON' | 'OFF')}
              size="small"
              fullWidth
              sx={{ ...FIELD_SX, gridColumn: { xs: '1 / -1', sm: 'auto' } }}
            >
              <MenuItem value="">
                <em>Unknown</em>
              </MenuItem>
              <MenuItem value="ON">On-premise</MenuItem>
              <MenuItem value="OFF">Off-premise</MenuItem>
            </TextField>
          </Box>

          {/* Who the rep actually deals with. For a brand-new account
              this is the most valuable thing they know, and before
              this it had nowhere to live but free-text notes. The
              depletions ingest never touches contacts, so a weekly
              file can't wipe them.

              A list of people, not one: a bar has an owner and a bar
              manager, and the second one was ending up in the notes
              where nothing can dial or search them. */}
          <SectionLabel>Contacts</SectionLabel>

          <ContactsEditor value={contacts} onChange={setContacts} />

          <SectionLabel>Notes</SectionLabel>

          <TextField
            label="Anything worth remembering (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            sx={FIELD_SX}
          />

          {/* Set on the tinted ground with a gold rule so it reads as
              the form telling you what happens next, rather than as
              one more line of grey text under the last input. */}
          <Box
            sx={{
              mt: 0.5,
              px: 1.75,
              py: 1.25,
              bgcolor: IVORY,
              borderRadius: 2,
              borderLeft: `2px solid ${GOLD}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.6)',
                lineHeight: 1.55,
              }}
            >
              This account will be marked <strong>manually added</strong> until it shows up in a
              depletions file, then it’s confirmed automatically.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      {/* Sticky on a phone: the form runs past the fold, and a rep
          shouldn't have to scroll back down to find the button that
          submits it. */}
      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${HAIRLINE}`,
          bgcolor: 'rgba(253, 249, 240, 0.75)',
          backdropFilter: 'blur(6px)',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            px: 2,
            color: 'rgba(15, 10, 7, 0.6)',
            '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.05)' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          // First click submits WITHOUT the confirm flag, so the
          // backend's duplicate check still runs. If it pushes back
          // (409 + candidates), the button becomes "Add anyway" and
          // the second click confirms. Auto-confirming just because
          // the dropdown had results would make the guard inert —
          // and the dropdown now has results almost always.
          onClick={() => submit(serverMatches !== null)}
          disabled={!canSubmit}
          disableElevation
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 14,
            borderRadius: 2,
            px: 2.5,
            py: 0.9,
            bgcolor: GOLD,
            boxShadow: '0 6px 16px -6px rgba(187, 140, 63, 0.8)',
            transition: 'background-color 160ms ease, box-shadow 160ms ease',
            '&:hover': { bgcolor: '#a67c34', boxShadow: '0 8px 20px -6px rgba(187, 140, 63, 0.9)' },
            '&.Mui-disabled': {
              bgcolor: 'rgba(187, 140, 63, 0.28)',
              color: '#fff',
              boxShadow: 'none',
            },
          }}
        >
          {submitting
            ? 'Adding…'
            : serverMatches !== null
              ? 'Add anyway — it’s different'
              : 'Add Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
